import {
  EmptystateArrowsLeftRightColored,
  EmptystateGridTilesColored,
  LogoOpentofuColored,
} from "components/icons/generated";
import { RunContext } from "components/Run/Context";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import { OPEN_TOFU_MIGRATION_GUIDE_URL } from "shared/Stack/OpenTofuMigration/constants";
import { getStackVendorSettingsUrl } from "shared/Stack/OpenTofuMigration/helpers";
import { IconComponent } from "types/Icon";
import { ObservabilityProcessingStatus } from "types/generated";

type RunObservabilityEmptyStateStatus = Exclude<
  ObservabilityProcessingStatus,
  ObservabilityProcessingStatus.Available
>;

type RunObservabilityEmptyStateContent = {
  title: string;
  description: string;
  icon: IconComponent;
};

type RunObservabilityEmptyStateProps = {
  status: RunObservabilityEmptyStateStatus;
};

const DATA_NOT_READY_CONTENT: RunObservabilityEmptyStateContent = {
  icon: EmptystateArrowsLeftRightColored,
  title: "Observability data is not ready yet",
  description:
    "The report appears after the run finishes and the data is processed, usually within a few minutes.",
};

const CONTENT: Record<RunObservabilityEmptyStateStatus, RunObservabilityEmptyStateContent> = {
  [ObservabilityProcessingStatus.NotEligible]: {
    icon: EmptystateGridTilesColored,
    title: "Run observability is not available for this run",
    description:
      "It reports where a run spent its time, which resources were slowest and what changed since the last run. Available for native OpenTofu and Terragrunt stacks when run observability is turned on for the account.",
  },
  [ObservabilityProcessingStatus.NotEligibleTerraformVendor]: {
    icon: LogoOpentofuColored,
    title: "Get run observability with OpenTofu",
    description:
      "It reports observability data so you can see what actually happened during a run, which makes it easier to debug runs that are slow or failing. To see the report, please migrate to OpenTofu native stacks.",
  },
  [ObservabilityProcessingStatus.PartialDataAvailable]: DATA_NOT_READY_CONTENT,
  [ObservabilityProcessingStatus.Processing]: DATA_NOT_READY_CONTENT,
  [ObservabilityProcessingStatus.Unavailable]: {
    icon: EmptystateGridTilesColored,
    title: "This run does not contain any observability data",
    description:
      "This run does not contain any observability data. It may simply be too old. Please contact us if the data should be here.",
  },
};

const RunObservabilityEmptyState = ({ status }: RunObservabilityEmptyStateProps) => {
  const { stack } = useTypedContext(RunContext);
  const { title, description, icon } = CONTENT[status];

  return (
    <EmptyState title={title} caption={description} icon={icon}>
      {status === ObservabilityProcessingStatus.NotEligibleTerraformVendor ? (
        <Box gap="medium">
          <LinkButton variant="secondary" href={OPEN_TOFU_MIGRATION_GUIDE_URL} target="_blank">
            Read the migration guide
          </LinkButton>
          <LinkButton variant="primary" to={getStackVendorSettingsUrl(stack.id)}>
            Migrate to OpenTofu
          </LinkButton>
        </Box>
      ) : (
        <LinkButton variant="primary" to="..">
          Go to History
        </LinkButton>
      )}
    </EmptyState>
  );
};

export default RunObservabilityEmptyState;
